import { useMutation, useQuery } from '@tanstack/react-query';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CreateSecretRequest,
  CreateVaultRequest,
  DeleteSecretV1SecretManagerSecretsSecretIdDeleteParams,
  DeleteVaultV1SecretManagerVaultsVaultIdDeleteParams,
  GetVaultSecretsV1SecretManagerVaultsVaultIdSecretsGetParams,
  GetVaultV1SecretManagerVaultsVaultIdGetParams,
  GetVaultsV1SecretManagerVaultsGetParams,
  ShareVaultRequest,
  UpdateSecretRequest,
  UpdateVaultRequest,
} from 'compliance-api';

export const SECRET_MANAGER_VAULTS_QUERY_KEY = 'vestra-compliance/secret-manager/vaults';
export const SECRET_MANAGER_VAULT_QUERY_KEY = 'vestra-compliance/secret-manager/vault';
export const SECRET_MANAGER_SECRETS_QUERY_KEY = 'vestra-compliance/secret-manager/secrets';

export const useVaultsQuery = (payload: GetVaultsV1SecretManagerVaultsGetParams) => {
  const { page, page_size, search } = payload;

  return useQuery({
    queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY, page, page_size, search],
    queryFn: async () => {
      const response = await api.v1.getVaultsV1SecretManagerVaultsGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    staleTime: 1000 * 60 * 60 * 1, // 1 hour
  });
};

export const useVaultQuery = (
  payload: GetVaultV1SecretManagerVaultsVaultIdGetParams,
  options?: { enabled?: boolean },
) => {
  const { enabled = true } = options || {};
  const { vaultId } = payload;

  return useQuery({
    queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY, vaultId],
    queryFn: async () => {
      const response = await api.v1.getVaultV1SecretManagerVaultsVaultIdGet(payload);
      return response.data;
    },
    enabled,
    staleTime: 1000 * 60 * 60 * 1, // 1 hour
  });
};

export const useVaultSecretsQuery = (
  payload: GetVaultSecretsV1SecretManagerVaultsVaultIdSecretsGetParams,
  options?: { enabled?: boolean },
) => {
  const { enabled = true } = options || {};
  const { vaultId, page, page_size, search } = payload;

  return useQuery({
    queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY, vaultId, page, page_size, search],
    queryFn: async () => {
      const response = await api.v1.getVaultSecretsV1SecretManagerVaultsVaultIdSecretsGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    enabled,
    staleTime: 1000 * 60 * 60 * 1, // 1 hour
  });
};

export const useCreateVaultMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateVaultRequest) => {
      const response = await withErrorHandling(
        api.v1.createVaultV1SecretManagerVaultsPost(payload),
        {
          loading: 'Creating vault...',
          success: 'Vault created successfully',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY] });
    },
  });
};

export const useShareVaultMutation = () => {
  return useMutation({
    mutationFn: async (payload: ShareVaultRequest) => {
      const response = await withErrorHandling(
        api.v1.shareVaultV1SecretManagerVaultsSharePatch(payload),
        {
          loading: 'Updating access...',
          success: 'Vault access updated',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
    },
  });
};

export const useCreateSecretMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateSecretRequest) => {
      const response = await withErrorHandling(
        api.v1.createSecretV1SecretManagerSecretsPost(payload),
        {
          loading: 'Creating secret...',
          success: 'Secret created successfully',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY] });
    },
  });
};

export const useUpdateSecretMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateSecretRequest) => {
      const response = await withErrorHandling(
        api.v1.updateSecretV1SecretManagerSecretsPatch(payload),
        {
          loading: 'Updating secret...',
          success: 'Secret updated',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY] });
    },
  });
};

export const useDeleteSecretMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeleteSecretV1SecretManagerSecretsSecretIdDeleteParams) => {
      const response = await withErrorHandling(
        api.v1.deleteSecretV1SecretManagerSecretsSecretIdDelete(payload),
        {
          loading: 'Deleting secret...',
          success: 'Secret deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY] });
    },
  });
};

export const useUpdateVaultMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateVaultRequest) => {
      const response = await withErrorHandling(
        api.v1.updateVaultV1SecretManagerVaultsPatch(payload),
        {
          loading: 'Updating vault...',
          success: 'Vault updated',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY] });
    },
  });
};

export const useDeleteVaultMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeleteVaultV1SecretManagerVaultsVaultIdDeleteParams) => {
      const response = await withErrorHandling(
        api.v1.deleteVaultV1SecretManagerVaultsVaultIdDelete(payload),
        {
          loading: 'Deleting vault...',
          success: 'Vault deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_VAULT_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SECRET_MANAGER_SECRETS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY] });
    },
  });
};
